import {
  CharacterizationBasis,
  Frequency,
  PackingGroup,
  PhysicalState,
  ProfileTestStatus,
  WasteProfileTestType,
} from "generated-graphql/graphql";

/**
 * Reserved for TABLE cells, where a blank reads as a broken row. Field grids
 * leave an unset value blank instead, matching the drawer's Details card.
 */
export const EMPTY_VALUE = "—";

/**
 * Every formatter here returns `undefined` for an unset value rather than a
 * placeholder, so a caller decides how absence should look: field grids pass
 * it straight through (blank, like Details), table cells apply `?? EMPTY_VALUE`.
 */
export const prettyPrintPhysicalState = (value?: PhysicalState | null): string | undefined => {
  switch (value) {
    case PhysicalState.Solid:
      return "Solid";
    case PhysicalState.Liquid:
      return "Liquid";
    case PhysicalState.Sludge:
      return "Sludge";
    case PhysicalState.Gas:
      return "Gas";
    case PhysicalState.Powder:
      return "Powder";
    default:
      return undefined;
  }
};

export const prettyPrintCharacterizationBasis = (
  value?: CharacterizationBasis | null
): string | undefined => {
  switch (value) {
    case CharacterizationBasis.Testing:
      return "Testing";
    case CharacterizationBasis.GeneratorKnowledge:
      return "Generator knowledge";
    default:
      return undefined;
  }
};

export const prettyPrintPackingGroup = (value?: PackingGroup | null): string | undefined => {
  switch (value) {
    case PackingGroup.I:
      return "I";
    case PackingGroup.Ii:
      return "II";
    case PackingGroup.Iii:
      return "III";
    case PackingGroup.None:
      return "None";
    default:
      return undefined;
  }
};

export const prettyPrintTestType = (value?: WasteProfileTestType | null): string | undefined => {
  switch (value) {
    case WasteProfileTestType.LabAnalysis:
      return "Lab analysis";
    case WasteProfileTestType.Recertification:
      return "Recertification";
    default:
      return undefined;
  }
};

export const prettyPrintTestStatus = (value?: ProfileTestStatus | null): string => {
  switch (value) {
    case ProfileTestStatus.Current:
      return "Current";
    case ProfileTestStatus.Due:
      return "Due";
    case ProfileTestStatus.Overdue:
      return "Overdue";
    default:
      return "Unknown";
  }
};

/**
 * The test status is the one genuinely status-like value in these sections, so
 * it is the one place color is spent. Outlined rather than filled, matching
 * `ProvisionalWasteStreamChip`. UNKNOWN is neutral: no cadence set is a
 * not-yet-configured state, not a problem.
 */
export const testStatusChipColor = (
  value?: ProfileTestStatus | null
): "success" | "warning" | "error" | "default" => {
  switch (value) {
    case ProfileTestStatus.Current:
      return "success";
    case ProfileTestStatus.Due:
      return "warning";
    case ProfileTestStatus.Overdue:
      return "error";
    default:
      return "default";
  }
};

/**
 * ONCE is deliberately absent: a re-certification cadence is inherently
 * recurring, and the backend rejects it (`addFrequency` throws on ONCE).
 */
export const TEST_FREQUENCY_OPTIONS = [
  { value: Frequency.Weekly, display: "Weeks" },
  { value: Frequency.Monthly, display: "Months" },
  { value: Frequency.Quarterly, display: "Quarters" },
  { value: Frequency.Yearly, display: "Years" },
];

export const prettyPrintTestFrequency = (
  frequency?: Frequency | null,
  every?: number | null
): string => {
  if (!frequency || every == null) return "No cadence set";
  const unit = TEST_FREQUENCY_OPTIONS.find((o) => o.value === frequency)?.display;
  if (!unit) return "No cadence set";
  const singular = unit.replace(/s$/, "").toLowerCase();
  return every === 1 ? `Every ${singular}` : `Every ${every} ${unit.toLowerCase()}`;
};

/**
 * `completeness.missing` carries raw field names from the API. Anything not
 * mapped falls back to the raw name rather than being dropped — a gap the
 * customer cannot see is worse than an unpolished label.
 */
const COMPLETENESS_LABELS: Record<string, string> = {
  specificGravity: "Specific gravity",
  profileDocument: "Profile document",
  physicalState: "Physical state",
  basis: "Characterization basis",
};

export const prettyPrintMissingField = (field: string): string =>
  COMPLETENESS_LABELS[field] ?? field;

/**
 * pH is stored as two independent bounds; either one can be set alone.
 */
export const formatPhRange = (low?: number | null, high?: number | null): string | undefined => {
  if (low == null && high == null) return undefined;
  if (low != null && high != null) return low === high ? `${low}` : `${low} – ${high}`;
  return low != null ? `≥ ${low}` : `≤ ${high}`;
};

export const formatPercentRange = (
  min?: number | null,
  max?: number | null
): string | undefined => {
  if (min == null && max == null) return undefined;
  if (min != null && max != null) return min === max ? `${min}%` : `${min}% – ${max}%`;
  return min != null ? `≥ ${min}%` : `≤ ${max}%`;
};

/**
 * `Date` scalars come off the wire as `YYYY-MM-DD`. Parsing that with the
 * local-time `Date` constructor shifts it a day west of UTC, so the format is
 * pinned to UTC — the same rule the rest of the drawer follows.
 */
export const formatProfileDate = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toLocaleDateString(undefined, { timeZone: "UTC" });
};
